/**
 * Shared per-field validators for check-in forms.
 * Each validator returns an error message string when the value is invalid/blank,
 * or undefined when valid. These are composed by per-asset-type validation files
 * under `CheckInForm/forms/*.validation.ts`.
 *
 * @see CheckInFormValues for the shared values interface consumed by all validators.
 */

import {validateScacCode} from '../../../util/ValidationUtils';

export interface CheckInFormValues {
  assetType: string;
  containerNumber?: string;
  chassisNumber?: string;
  chassisLicensePlateNumber?: string;
  sealNumber?: string;
  trailerNumber?: string;
  driverFirstName?: string;
  driverLastName?: string;
  driverLicenseNumber?: string;
  truckLicensePlateNumber?: string;
  scacCode?: string;
  assetSize?: string;
  notes?: string;
}

// ─── Per-field validators ─────────────────────────────────────────────────────

/**
 * Returns the error message if the value is blank, otherwise undefined.
 * Used by all required-field checks below.
 */
const required = (value: string | undefined, message: string): string | undefined =>
  !value?.trim() ? message : undefined;

export const validateRequiredContainerNumber = (v: string | undefined): string | undefined => required(v, 'Please enter a container number.');
export const validateRequiredSealNumber = (v: string | undefined): string | undefined => required(v, 'Please enter a seal number.');
export const validateRequiredChassisNumber = (v: string | undefined): string | undefined => required(v, 'Please enter a chassis number.');
export const validateRequiredTrailerNumber = (v: string | undefined): string | undefined => required(v, 'Please enter a trailer number.');
export const validateRequiredDriverFirstName = (v: string | undefined): string | undefined => required(v, "Please enter the driver's first name.");
export const validateRequiredDriverLastName = (v: string | undefined): string | undefined => required(v, "Please enter the driver's last name.");
export const validateRequiredDriverLicenseNumber = (v: string | undefined): string | undefined => required(v, 'Please enter the driver license number of the driver.');
export const validateRequiredAssetSize = (v: string | undefined): string | undefined => required(v, 'Please enter an asset size.');
export const validateRequiredTruckLicensePlateNumber = (v: string | undefined): string | undefined => required(v, "Please enter the truck's license plate number.");

/**
 * Validates the optional scacCode field.
 * Returns undefined when blank (field is optional).
 * Returns an error message when present but malformed.
 */
export const validateOptionalScacCode = (v: string | undefined): string | undefined => {
  const trimmed = v?.trim();
  if (!trimmed) return undefined;
  return validateScacCode(trimmed) ? undefined : 'SCAC code must be alphanumeric and be 2 to 4 characters long.';
};
